import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface LeaderboardEntry {
  rank: number;
  name: string;
  initials: string;
  level: string;
  levelColor: string;
  points: number;
  coursesCompleted: number;
  certificatesEarned: number;
  progressToNextLevel: number;
}

export interface DepartmentPerformance {
  department: string;
  totalUsers: number;
  usersWithEnrollments: number;
  completionPercentage: number;
  totalPercentage: number;
}

export interface CompletionTrend {
  month: string;
  enrolled: number;
  completed: number;
  target: number; // always 85
}

export interface CertificationDistribution {
  type: string;
  percentage: number;
  color: string;
}

export interface SkillAssessment {
  skill: string;
  score: number;
  users: number;
}

export interface UserReportEntry {
  userId: string;
  fullName: string;
  email: string;
  role: string;
  department: string;
  jobTitle: string;
  status: string;
  joinDate: string;
  lastLogin: string;
  coursesEnrolled: number;
  coursesCompleted: number;
  certificatesEarned: number;
  totalPoints: number;
}

export interface CourseCompletionEntry {
  courseId: string;
  title: string;
  category: string;
  totalEnrollments: number;
  completed: number;
  completionRate: number;
  avgTimeSpent: number;
  courseDuration: number;
}

export interface LearningEngagement {
  totalActiveUsers: number;
  usersWithEnrollments: number;
  totalEnrollments: number;
  totalTimeSpent: number;
  avgTimePerUser: number;
  activeUsersLast7Days: number;
}

export interface DashboardKpiStats {
  title: string;
  value: number;
  increase: string;
  iconType: 'book' | 'alert' | 'check' | 'users';
}

export interface LearningHoursData {
  week: string;
  hours: number;
}

export interface LearningProgress {
  totalHours: number;
  progressPercentage: number;
  trend: 'up' | 'down' | 'neutral';
  trendValue: number;
  currentMonth: string;
}

export interface TopCourse {
  name: string;
  enrolled: number;
  total: number;
}

export interface CombinedAnalyticsResponse {
  leaderboard: LeaderboardEntry[];
  departmentPerformance: DepartmentPerformance[];
  completionTrends: CompletionTrend[];
  certificationDistribution: CertificationDistribution[];
  skillsAssessment: SkillAssessment[];
}

export interface LoginActivityEntry {
  date: string;
  activeUsers: number;
}

export interface EnrollmentTimelineEntry {
  week: string;
  enrollments: number;
  completions: number;
}

export interface CategoryBreakdownEntry {
  category: string;
  courseCount: number;
  enrollments: number;
  completionRate: number;
  color: string;
}

export interface ProgressDistribution {
  notStarted: number;
  beginner: number;
  intermediate: number;
  advanced: number;
  completed: number;
}

export interface UserGrowthEntry {
  month: string;
  newUsers: number;
  totalUsers: number;
}

export interface RoleDistributionEntry {
  role: string;
  count: number;
  color: string;
}

export interface ActivityHeatmapEntry {
  day: string;
  dayNum: number;
  hour: number;
  count: number;
}

export interface DateFilter {
  fromDate?: string;
  toDate?: string;
}

export interface UserReportFilter extends DateFilter {
  department?: string;
}

export interface AnalyticsData {
  combinedAnalytics: CombinedAnalyticsResponse | null;
  userReportData: UserReportEntry[] | null;
  courseCompletionData: CourseCompletionEntry[] | null;
  learningEngagement: LearningEngagement | null;
  dashboardStats: DashboardKpiStats[] | null;
  learningHours: LearningHoursData[] | null;
  learningProgress: LearningProgress | null;
  topCourses: TopCourse[] | null;
  loginActivity: LoginActivityEntry[] | null;
  enrollmentTimeline: EnrollmentTimelineEntry[] | null;
  categoryBreakdown: CategoryBreakdownEntry[] | null;
  progressDistribution: ProgressDistribution | null;
  userGrowth: UserGrowthEntry[] | null;
  roleDistribution: RoleDistributionEntry[] | null;
  activityHeatmap: ActivityHeatmapEntry[] | null;
}

// ===== API Response Interface =====
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ===== HELPER FUNCTIONS =====

const buildDateQuery = (filter?: DateFilter): string => {
  if (!filter) return '';
  const params = new URLSearchParams();
  if (filter.fromDate) params.append('fromDate', filter.fromDate);
  if (filter.toDate) params.append('toDate', filter.toDate);
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

const buildUserReportQuery = (filter?: UserReportFilter): string => {
  if (!filter) return '';
  const params = new URLSearchParams();
  if (filter.fromDate) params.append('fromDate', filter.fromDate);
  if (filter.toDate) params.append('toDate', filter.toDate);
  if (filter.department) params.append('department', filter.department);
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

// ===== API ENDPOINTS =====
const ENDPOINTS = {
  COMBINED_ANALYTICS: '/admin/reports/analytics',
  USER_DATA: '/admin/reports/user-data',
  COURSE_COMPLETION_DATA: '/admin/reports/course-completion-data',
  LEARNING_ENGAGEMENT_DATA: '/admin/reports/learning-engagement-data',
  DASHBOARD_STATS: '/admin/dashboard/stats',
  LEARNING_HOURS: '/admin/dashboard/learning-hours',
  LEARNING_PROGRESS: '/admin/dashboard/learning-progress',
  TOP_COURSES: '/admin/dashboard/top-courses',
  LOGIN_ACTIVITY: '/admin/reports/login-activity',
  ENROLLMENT_TIMELINE: '/admin/reports/enrollment-timeline',
  CATEGORY_BREAKDOWN: '/admin/reports/category-breakdown',
  PROGRESS_DISTRIBUTION: '/admin/reports/progress-distribution',
  USER_GROWTH: '/admin/reports/user-growth',
  ROLE_DISTRIBUTION: '/admin/reports/role-distribution',
  ACTIVITY_HEATMAP: '/admin/reports/activity-heatmap',
};

// ===== HELPER: safe error extraction =====
function extractErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

// ===== SERVICE FUNCTIONS =====

export const getCombinedAnalytics = async (): Promise<{
  success: boolean;
  data?: CombinedAnalyticsResponse;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CombinedAnalyticsResponse>>(ENDPOINTS.COMBINED_ANALYTICS);
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch combined analytics') } };
  }
};

export const getUserReportData = async (filter?: UserReportFilter): Promise<{
  success: boolean;
  data?: UserReportEntry[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildUserReportQuery(filter);
    const response = await httpClient.get<ApiResponse<UserReportEntry[]>>(
      `${ENDPOINTS.USER_DATA}${queryParams}`
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: unknown) {
    
    const message = error instanceof Error ? error.message : 'Failed to fetch user report data';
    return { success: false, error: { message } };
  }
};

export const getCourseCompletionData = async (filter?: DateFilter): Promise<{
  success: boolean;
  data?: CourseCompletionEntry[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseCompletionEntry[]>>(`${ENDPOINTS.COURSE_COMPLETION_DATA}${buildDateQuery(filter)}`);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch course completion data') } };
  }
};

export const getLearningEngagementData = async (filter?: DateFilter): Promise<{
  success: boolean;
  data?: LearningEngagement;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<LearningEngagement>>(`${ENDPOINTS.LEARNING_ENGAGEMENT_DATA}${buildDateQuery(filter)}`);
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch learning engagement data') } };
  }
};

export const getDashboardStats = async (): Promise<{
  success: boolean;
  data?: DashboardKpiStats[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<DashboardKpiStats[]>>(ENDPOINTS.DASHBOARD_STATS);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch dashboard stats') } };
  }
};

export const getLearningHours = async (): Promise<{
  success: boolean;
  data?: LearningHoursData[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<LearningHoursData[]>>(ENDPOINTS.LEARNING_HOURS);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch learning hours') } };
  }
};

export const getLearningProgress = async (): Promise<{
  success: boolean;
  data?: LearningProgress;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<LearningProgress>>(ENDPOINTS.LEARNING_PROGRESS);
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch learning progress') } };
  }
};

export const getTopCourses = async (limit: number = 5): Promise<{
  success: boolean;
  data?: TopCourse[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<TopCourse[]>>(`${ENDPOINTS.TOP_COURSES}?limit=${limit}`);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch top courses') } };
  }
};

export const getLoginActivity = async (): Promise<{ success: boolean; data?: LoginActivityEntry[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<LoginActivityEntry[]>>(ENDPOINTS.LOGIN_ACTIVITY);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch login activity') } };
  }
};

export const getEnrollmentTimeline = async (): Promise<{ success: boolean; data?: EnrollmentTimelineEntry[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<EnrollmentTimelineEntry[]>>(ENDPOINTS.ENROLLMENT_TIMELINE);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch enrollment timeline') } };
  }
};

export const getCategoryBreakdown = async (): Promise<{ success: boolean; data?: CategoryBreakdownEntry[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<CategoryBreakdownEntry[]>>(ENDPOINTS.CATEGORY_BREAKDOWN);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch category breakdown') } };
  }
};

export const getProgressDistribution = async (): Promise<{ success: boolean; data?: ProgressDistribution; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<ProgressDistribution>>(ENDPOINTS.PROGRESS_DISTRIBUTION);
    return { success: true, data: response.data, error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch progress distribution') } };
  }
};

export const getUserGrowth = async (): Promise<{ success: boolean; data?: UserGrowthEntry[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<UserGrowthEntry[]>>(ENDPOINTS.USER_GROWTH);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch user growth') } };
  }
};

export const getRoleDistribution = async (): Promise<{ success: boolean; data?: RoleDistributionEntry[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<RoleDistributionEntry[]>>(ENDPOINTS.ROLE_DISTRIBUTION);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch role distribution') } };
  }
};

export const getActivityHeatmap = async (): Promise<{ success: boolean; data?: ActivityHeatmapEntry[]; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<ApiResponse<ActivityHeatmapEntry[]>>(ENDPOINTS.ACTIVITY_HEATMAP);
    return { success: true, data: response.data || [], error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch activity heatmap') } };
  }
};

// ===== AGGREGATED FETCH =====

export const fetchAllAnalytics = async (
  dateFilter?: DateFilter,
  userFilter?: UserReportFilter
): Promise<{
  success: boolean;
  data?: AnalyticsData;
  error?: { message: string } | null;
}> => {
  try {
    const results = await Promise.allSettled([
      getCombinedAnalytics(),
      getUserReportData(userFilter),
      getCourseCompletionData(dateFilter),
      getLearningEngagementData(dateFilter),
      getDashboardStats(),
      getLearningHours(),
      getLearningProgress(),
      getTopCourses(),
      getLoginActivity(),
      getEnrollmentTimeline(),
      getCategoryBreakdown(),
      getProgressDistribution(),
      getUserGrowth(),
      getRoleDistribution(),
      getActivityHeatmap(),
    ]);

    const extractData = <T>(result: PromiseSettledResult<{ success: boolean; data?: T }>): T | null => {
      if (result.status === 'fulfilled' && result.value.success && result.value.data) {
        return result.value.data;
      }
      return null;
    };

    const analyticsData: AnalyticsData = {
      combinedAnalytics: extractData(results[0]),
      userReportData: extractData(results[1]),
      courseCompletionData: extractData(results[2]),
      learningEngagement: extractData(results[3]),
      dashboardStats: extractData(results[4]),
      learningHours: extractData(results[5]),
      learningProgress: extractData(results[6]),
      topCourses: extractData(results[7]),
      loginActivity: extractData(results[8]),
      enrollmentTimeline: extractData(results[9]),
      categoryBreakdown: extractData(results[10]),
      progressDistribution: extractData(results[11]),
      userGrowth: extractData(results[12]),
      roleDistribution: extractData(results[13]),
      activityHeatmap: extractData(results[14]),
    };

    return { success: true, data: analyticsData, error: null };
  } catch (error: unknown) {
    return { success: false, error: { message: extractErrorMessage(error, 'Failed to fetch analytics data') } };
  }
};
